import React, { useState } from 'react';
import { Device } from 'DeviceUtils';
import SuccessPage from 'Pages/SuccessPage';
import { t } from 'Locale';
import { ConsentsDetailed } from 'Pages';
import AppInfo from '../AppInfo';
import ClearCache from '../ClearCache';
import AccountInfo from '../AccountInfo';
import { NetworkSettings } from '../NetworkSettings';
import { VideoQualitySettings } from '../VideoQualitySettings';
import { LanguageSettings } from '../LanguageSettings';
import { Parental } from '../Parental';
import DeviceManagement from '../DeviceManagement/DeviceManagement';
import ChangePassword from '../PasswordManagement/Pages/ChangePassword';
import NewUsername from '../PasswordManagement/Pages/NewUsername';
import { pages } from '../PasswordManagement/PasswordManagement';

import { FavoritesSettings } from '../FavoriteSettings';

const filterSectionsByUserRights = (sections, userData, consentRights) =>
    consentRights
        ? sections.filter(
              (section) =>
                  section.requiresLogin === undefined ||
                  (section.requiresLogin === true && userData)
          )
        : sections.filter(
              (section) =>
                  section.title !== 'SETTINGS.CONSENTS_MENU_ITEM_MOBILES' &&
                  (section.requiresLogin === undefined ||
                      (section.requiresLogin === true && userData))
          );

const ChangePasswordPages = (props) => {
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    if (showSuccessDialog) {
        return (
            <SuccessPage
                message={t('COMMON.PASSWORD_SET_SUCCESS')}
                onContinue={() => setShowSuccessDialog(false)}
            />
        );
    }
    return (
        <ChangePassword
            {...props}
            displayHeader={false}
            onSaved={() => setShowSuccessDialog(true)}
        />
    );
};

const ChangeUsernamePages = (props) => {
    const [isSuccess, setIsSuccess] = useState(false);
    if (isSuccess) {
        return (
            <SuccessPage
                message={t('SETTINGS.SAVE_USERNAME_CONFIRM_TXT')}
                buttonTitle={t('COMMON.OK')}
            />
        );
    }
    return <NewUsername {...props} displayHeader={false} onSaved={() => setIsSuccess(true)} />;
};

const contentSettings = Device.isWeb
    ? [
          {
              title: 'SETTINGS.FAVOURITE_CHANNELS',
              requiresLogin: true,
              navigationOptions: {
                  route: 'FavoriteSettings'
              },
              renderDetail: (props) => <FavoritesSettings {...props} />
          },
          {
              title: 'SETTINGS.PARENTAL_CONTROL',
              requiresLogin: true,
              navigationOptions: {
                  route: 'SettingsParental'
              },
              renderDetail: (props) => <Parental {...props} />
          },
          {
              title: 'SETTINGS.LANGUAGE',
              navigationOptions: {
                  route: 'LanguageSettings'
              },
              renderDetail: (props) => <LanguageSettings {...props} />
          }
      ].filter(Boolean)
    : [
          {
              title: 'SETTINGS.FAVOURITE_CHANNELS',
              requiresLogin: true,
              navigationOptions: {
                  route: 'FavoriteSettings'
              },
              renderDetail: (props) => <FavoritesSettings {...props} />
          },
          {
              title: 'SETTINGS.PARENTAL_CONTROL',
              requiresLogin: true,
              navigationOptions: {
                  route: 'SettingsParental'
              },
              renderDetail: (props) => <Parental {...props} />
          },
          {
              title: 'SETTINGS.DATA_SAVER.MENU_ITEM',
              requiresLogin: true,
              navigationOptions: {
                  route: 'VideoQualitySettings'
              },
              renderDetail: (props) => <VideoQualitySettings {...props} />
          },
          {
              title: 'SETTINGS.LANGUAGE',
              navigationOptions: {
                  route: 'LanguageSettings'
              },
              renderDetail: (props) => <LanguageSettings {...props} />
          }
      ].filter(Boolean);

const getAccountSettings = (subscriberType) => [
    {
        title: 'SETTINGS.DEVICES',
        requiresLogin: true,
        navigationOptions: {
            route: 'DeviceManagement'
        },
        renderDetail: (props) => <DeviceManagement {...props} />
    },
    {
        title: 'SETTINGS.CHANGE_PASSWORD',
        requiresLogin: true,
        navigationOptions: {
            route: 'PasswordManagement',
            params: {
                initialRoute: pages.changePassword
            }
        },
        renderDetail: (props) => <ChangePasswordPages {...props} />
    },
    {
        title: 'COMMON.CHANGE_USERNAME',
        requiresLogin: true,
        navigationOptions: {
            route: 'PasswordManagement',
            params: {
                initialRoute: pages.changeUsername
            }
        },
        renderDetail: (props) => <ChangeUsernamePages {...props} />
    },
    {
        title: 'COMMON.ACCOUNT_INFO_MOBILES',
        requiresLogin: true,
        navigationOptions: {
            route: 'AccountInfo',
            params: {}
        },
        renderDetail: (props) => <AccountInfo {...props} />
    },
    ...(subscriberType !== 'BENEFIT'
        ? [
              {
                  title: 'SETTINGS.CONSENTS_MENU_ITEM_MOBILES',
                  requiresLogin: true,
                  navigationOptions: {
                      route: 'NativeConsents',
                      params: {}
                  },
                  renderDetail: (props) => <ConsentsDetailed {...props} />
              }
          ]
        : []),
    // Platform specific settings
    ...(Device.isWeb
        ? [
              {
                  title: t('COMMON.CLEAR_CACHE'),
                  navigationOptions: {
                      route: 'ClearCache'
                  },
                  renderDetail: (props) => <ClearCache {...props} />
              }
          ]
        : [])
];

if (!Device.isWeb) {
    contentSettings.splice(Math.min(2, contentSettings.length - 1), 0, {
        title: 'SETTINGS.NETWORK',
        requiresLogin: true,
        navigationOptions: {
            route: 'NetworkSettings'
        },
        renderDetail: (props) => <NetworkSettings {...props} />
    });
}

export const getSettingsSections = (userData, consentRights, subscriberType) =>
    [
        {
            title: 'SETTINGS.CONTENT_SETTINGS',
            data: filterSectionsByUserRights(contentSettings, userData, consentRights)
        },
        {
            title: 'SETTINGS.ACCOUNT_SETTINGS',
            data: filterSectionsByUserRights(
                getAccountSettings(subscriberType),
                userData,
                consentRights,
                subscriberType
            )
        },
        !Device.isWeb && {
            title: '',
            data: [
                {
                    title: `${t('SETTINGS.APP_INFO')}`,
                    navigationOptions: {
                        route: 'AppInfo'
                    },
                    renderDetail: (props) => <AppInfo {...props} />
                },
                {
                    title: `${t('COMMON.CLEAR_CACHE')}`,
                    navigationOptions: {
                        route: 'ClearCache',
                        headerShown: false,
                        headerVisible: false
                    },
                    renderDetail: (props) => <ClearCache {...props} />
                }
            ]
        }
    ].filter(Boolean);
